// Which way a move through the directory goes, from where it starts and where
// it lands, and nothing else. Every source of navigation (a tile, the crumb, the
// ticker, a swipe, the menu, search, netti, Back and Forward) asks the same
// question, so the content always travels the way the directory did:
//
//   deeper     +1  the new place is inside the old one (a folder opened)
//   shallower  −1  the old place is inside the new one (the crumb, Backspace)
//   sideways   ±1  siblings: the sign of the step along the ticker, the short
//                  way round when it wraps (last → first reads as forward)
//   elsewhere  +1  an unrelated jump (search, the menu, netti) moves forward
//
// Paths are shell paths ("/projects/p1/vision"); siblings are the paths of the
// level's visible siblings, in ticker order.

export type Dir = 1 | -1;

const norm = (p: string) => (p.replace(/\/+$/, "") || "/");

export function isInside(child: string, parent: string): boolean {
  const c = norm(child);
  const p = norm(parent);
  if (p === "/") return c !== "/";
  return c.startsWith(p + "/");
}

export function parentOf(path: string): string | null {
  const p = norm(path);
  if (p === "/") return null;
  const i = p.lastIndexOf("/");
  return i <= 0 ? "/" : p.slice(0, i);
}

/** the step along a wrapping ticker of `n` items from `from` to `to`, the short way. */
export function wrapStep(from: number, to: number, n: number): Dir {
  if (n < 2 || from === to) return 1;
  const fwd = (to - from + n) % n;
  const back = (from - to + n) % n;
  return fwd <= back ? 1 : -1;
}

export type Relation = { kind: "same" | "deeper" | "shallower" | "sideways" | "elsewhere"; dir: Dir };

/** how two places relate along the paths, and the direction that implies. */
export function relationOf(from: string, to: string, siblings?: readonly string[]): Relation {
  const a = norm(from);
  const b = norm(to);
  if (a === b) return { kind: "same", dir: 1 };
  if (isInside(b, a)) return { kind: "deeper", dir: 1 };
  if (isInside(a, b)) return { kind: "shallower", dir: -1 };
  if (siblings && parentOf(a) === parentOf(b)) {
    const i = siblings.indexOf(a);
    const j = siblings.indexOf(b);
    if (i >= 0 && j >= 0) return { kind: "sideways", dir: wrapStep(i, j, siblings.length) };
  }
  return { kind: "elsewhere", dir: directionOf(a, b) };
}

export function directionOf(from: string, to: string, siblings?: readonly string[]): Dir {
  const a = norm(from);
  const b = norm(to);
  if (a === b) return 1;
  if (isInside(b, a)) return 1;
  if (isInside(a, b)) return -1;
  if (siblings && parentOf(a) === parentOf(b)) {
    const i = siblings.indexOf(a);
    const j = siblings.indexOf(b);
    if (i >= 0 && j >= 0) return wrapStep(i, j, siblings.length);
  }
  // cousins: compare where they part — the branch further along the parent's
  // children is "ahead". Without the siblings to say so, depth decides: going
  // up and over reads as back, going down and over as forward.
  const da = a.split("/").length;
  const db = b.split("/").length;
  if (db < da) return -1;
  return 1;
}
